import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from 'react-table';
import { FlowExecution, Project } from '../common/dataTypes/Jetstream';
import { DateRangeFilter } from '../common/DateRangeFilter/DateRangeFilter';
import { MultiSelectFilter } from '../common/MultiSelectFilter/MultiSelectFilter';
import { useDisplayPreferences } from '../common/hooks/useDisplayPreferences';
import { formatDateWithTz, computeRunTime } from '../common/utils/dateFormat';
import { Breakpoint, Large, Small } from '../jet-ui/Breakpoints/Breakpoint';
import { Capsule } from '../jet-ui/Capsule/Capsule';
import { Card } from '../jet-ui/Card/Card';
import { JetTable } from '../jet-ui/JetTable/JetTable';
import { Spinner } from '../jet-ui/Spinner/Spinner';
import { ExecutionCard } from './ExecutionCard';

interface RecentTableProps {
  data: FlowExecution[];
  rowsPerPage?: number;
  pages: number;
  onPageChange: (pageIndex: number) => void;
  pageIndex: number;
  loading?: boolean;
  projects: Project[];
  projectFilter: string[];
  onProjectFilterChange: (v: string[]) => void;
  flowNames: string[];
  flowFilter: string[];
  onFlowFilterChange: (v: string[]) => void;
  userNames: string[];
  userFilter: string[];
  onUserFilterChange: (v: string[]) => void;
  statusNames: string[];
  statusFilter: string[];
  onStatusFilterChange: (v: string[]) => void;
  startedFrom: string | null;
  startedTo: string | null;
  onStartedChange: (from: string | null, to: string | null) => void;
  endedFrom: string | null;
  endedTo: string | null;
  onEndedChange: (from: string | null, to: string | null) => void;
};

export const RecentTable: React.FC<RecentTableProps> = (props) => {

  const { prefs } = useDisplayPreferences();
  const visibleColumns = prefs.monitorColumns;
  const tz = prefs.timezone;

  const getStatusColor = React.useCallback((status: string) => {
    switch (status) {
      case 'Succeeded':
        return 'good';
      case 'Running':
        return 'warning';
      case 'Canceled':
        return 'neutral';
    }

    return 'error';
  }, []);

  // Define all possible columns with their keys
  const allColumns = React.useMemo((): (Column<FlowExecution> & { columnKey: string })[] => [
    {
      columnKey: 'flow',
      Header: () => <MultiSelectFilter label="Flow" options={props.flowNames} selected={props.flowFilter} onChange={props.onFlowFilterChange} />,
      accessor: 'flowName',
      width: '20%',
      disableSortBy: true,
      Cell: cell => (<Link to={`/execution/${cell.row.original.flowExecutionId}/detail`}>{cell.value}</Link>)
    },
    {
      columnKey: 'project',
      Header: () => <MultiSelectFilter label="Project" options={props.projects.map(p => p.name)} selected={props.projectFilter} onChange={props.onProjectFilterChange} />,
      accessor: 'projectName' as any,
      width: '12%',
      disableSortBy: true
    },
    {
      columnKey: 'id',
      Header: 'ID',
      accessor: 'flowExecutionId',
      width: '70px',
      disableSortBy: true
    },
    {
      columnKey: 'jobs',
      Header: 'Jobs',
      accessor: (execution) => execution.structure.jobs.length,
      width: '60px',
      disableSortBy: true
    },
    {
      columnKey: 'started',
      Header: () => <DateRangeFilter label="Started" fromDate={props.startedFrom} toDate={props.startedTo} onChange={props.onStartedChange} />,
      accessor: 'started',
      width: '18%',
      disableSortBy: true,
      Cell: ({ value }) => formatDateWithTz(value, tz)
    },
    {
      columnKey: 'finished',
      Header: () => <DateRangeFilter label="Finished" fromDate={props.endedFrom} toDate={props.endedTo} onChange={props.onEndedChange} />,
      accessor: 'ended',
      width: '18%',
      disableSortBy: true,
      Cell: ({ value }) => formatDateWithTz(value, tz)
    },
    {
      columnKey: 'runTime',
      Header: 'Run Duration',
      id: 'runTime',
      accessor: (execution) => computeRunTime(execution.started, execution.ended),
      width: '90px',
      disableSortBy: true
    },
    {
      columnKey: 'by',
      Header: () => <MultiSelectFilter label="By" options={props.userNames} selected={props.userFilter} onChange={props.onUserFilterChange} />,
      accessor: 'userId',
      width: '10%',
      disableSortBy: true
    },
    {
      columnKey: 'status',
      Header: () => <MultiSelectFilter label="Status" options={props.statusNames} selected={props.statusFilter} onChange={props.onStatusFilterChange} />,
      accessor: 'status',
      width: '100px',
      disableSortBy: true,
      Cell: ({ value }) => (<Capsule color={getStatusColor(value)}>{value}</Capsule>)
    }
  ], [props.projects, props.projectFilter, props.flowNames, props.flowFilter, props.userNames, props.userFilter, props.statusNames, props.statusFilter, props.startedFrom, props.startedTo, props.endedFrom, props.endedTo, tz]);

  // Filter columns based on display preferences
  const columns = React.useMemo(
    () => allColumns.filter(col => visibleColumns.includes(col.columnKey)),
    [allColumns, visibleColumns]
  );

  return (
    <Breakpoint>
      <Small>
        {props.loading && <div className="Monitor__Loading"><Spinner /></div>}
        {props.data.map(execution => <ExecutionCard execution={execution} />)}
        {(props.data.length === 0 && !props.loading) && <Card>No recent flows found.</Card>}
      </Small>
      <Large>
        <JetTable data={props.data} columns={columns} emptyMessage="No recent flows found" loading={props.loading} pages={props.pages}
          onManualPageChange={props.onPageChange} rowsPerPage={props.rowsPerPage ?? 20} pageIndex={props.pageIndex} />
      </Large>
    </Breakpoint>
  );
};
